import ApiService from 'api-client/ApiService'
import { NovaServersResponse, ServerLite } from 'app/plugins/openstack/components/vms/model'

class Nova extends ApiService {
  public getClassName() {
    return 'nova'
  }

  protected async getEndpoint() {
    return this.client.keystone.getServiceEndpoint('nova', 'public')
  }

  flavorsUrl = () => `/flavors`
  instancesUrl = () => `/servers`
  hypervisorsUrl = () => `/os-hypervisors`
  sshKeysUrl = () => `/os-keypairs`

  checkHealth = async () => {
    return this.client.basicGet<any>({
      url: `/`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'checkHealth',
      },
    })
  }

  getVersionDetails = async () => {
    const response = await this.client.basicGet<any>({
      url: `/`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getNovaVersion',
      },
    })
    return response
  }

  getFlavors = async () => {
    // const url = `${this.flavorsUrl()}/detail?is_public=no`
    const response = await this.client.basicGet<any>({
      url: `/flavors/detail?is_public=None`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackFlavors',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
            // 'X-OpenStack-Nova-API-Version': '2.61',
          },
        },
      },
    })
    return response.flavors
  }

  getFlavor = async (flavorId) => {
    const response = await this.client.basicGet<any>({
      url: `/flavors/${flavorId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackFlavor',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
            // 'X-OpenStack-Nova-API-Version': '2.61',
          },
        },
      },
    })
    return response.flavor
  }

  createFlavor = async (body) => {
    const response = await this.client.basicPost<any>({
      url: `/flavors`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createFlavor',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response.flavor
  }

  createFlavorMetadata = async ({ id, body }) => {
    const response = await this.client.basicPost<any>({
      url: `/flavors/${id}/os-extra_specs`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createFlavorMetadata',
      },
    })
    return response
  }

  updateFlavorMetadata = async ({ id, body, key }) => {
    const response = await this.client.basicPut<any>({
      url: `/flavors/${id}/os-extra_specs/${key}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateFlavorMetadata',
      },
    })
    return response
  }

  deleteFlavorMetadata = async ({ id, key }) => {
    return this.client.basicDelete<any>({
      url: `/flavors/${id}/os-extra_specs/${key}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteFlavorMetadata',
      },
    })
  }

  getFlavorTenants = async ({ id }) => {
    const response = await this.client.basicGet<any>({
      url: `/flavors/${id}/os-flavor-access`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getFlavorTenants',
      },
    })
    return response.flavor_access
  }

  addFlavorTenant = async ({ id, body }) => {
    const response = await this.client.basicPost<any>({
      url: `/flavors/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'addFlavorTenant',
      },
    })
    return response
  }

  removeFlavorTenant = async ({ id, body }) => {
    const response = await this.client.basicPost<any>({
      url: `/flavors/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'removeFlavorTenant',
      },
    })
    return response
  }

  deleteFlavor = async (id) => {
    return this.client.basicDelete<any>({
      url: `/flavors/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteFlavor',
      },
    })
  }

  getInstances = async () => {
    const response = await this.client.basicGet<NovaServersResponse>({
      url: `/servers/detail`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackVms',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.servers
  }

  // Get the list of all VM - only name, id
  getAllInstancesLite = async (
    queryParams = '',
    searchParams?: Record<string, string>,
  ): Promise<ServerLite[]> => {
    // Build query string
    const params = new URLSearchParams(queryParams)

    // Add search parameters
    if (searchParams) {
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value) {
          params.append(key, value)
        }
      })
    }

    const queryString = params.toString()
    const response = await this.client.basicGet<NovaServersResponse>({
      url: queryString ? `/servers?${queryString}` : `/servers`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getAllInstancesLite',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.servers
  }

  getAllInstances = async () => {
    const response = await this.client.basicGet<NovaServersResponse>({
      url: `/servers/detail?all_tenants=True`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getAllOpenstackVms',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.servers
  }

  getInstancesSince = async (timestamp) => {
    const response = await this.client.basicGet<NovaServersResponse>({
      url: `/servers/detail?changes-since=${timestamp}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackVmsSince',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.servers
  }

  getAllInstancesSince = async (timestamp) => {
    const response = await this.client.basicGet<NovaServersResponse>({
      url: `/servers/detail?all_tenants=True&changes-since=${timestamp}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getAllOpenstackVmsSince',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.servers
  }

  getSelfInstancesSince = async (timestamp, userId) => {
    const response = await this.client.basicGet<NovaServersResponse>({
      url: `/servers/detail?user_id=${userId}&changes-since=${timestamp}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getSelfOpenstackVmsSince',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.servers
  }

  getInstancesForReservationId = async (reservationId) => {
    const response = await this.client.basicGet<NovaServersResponse>({
      url: `/servers/detail?reservation_id=${reservationId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackVmsWithReservationId',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.servers
  }

  getInstancesForHost = async (hostId) => {
    const response = await this.client.basicGet<NovaServersResponse>({
      url: `/servers/detail?all_tenants=True&deleted=False&host=${hostId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getHostOpenstackVms',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.servers
  }

  getInstancesForUser = async (userId) => {
    const response = await this.client.basicGet<NovaServersResponse>({
      url: `/servers/detail?&user_id=${userId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getUserOpenstackVms',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.servers
  }

  getInstancesPaginated = async ({
    limit = 100,
    marker,
    sortKey = 'created_at',
    sortDir = 'desc',
    allTenants = false,
    userId,
    searchParams,
    changesSince,
  }: {
    limit?: number
    marker?: string
    sortKey?: string
    sortDir?: 'asc' | 'desc'
    allTenants?: boolean
    userId?: string
    searchParams?: Record<string, string>
    changesSince?: string
  }) => {
    // Build query parameters
    const queryParams = new URLSearchParams()

    if (limit) {
      queryParams.append('limit', limit.toString())
    }

    if (marker) {
      queryParams.append('marker', marker)
    }

    if (sortKey) {
      queryParams.append('sort_key', sortKey)
    }

    if (sortDir) {
      queryParams.append('sort_dir', sortDir)
    }

    if (allTenants) {
      queryParams.append('all_tenants', 'True')
    }

    if (userId) {
      queryParams.append('user_id', userId)
    }

    if (changesSince) {
      queryParams.append('changes-since', changesSince)
    }

    // Add search parameters
    if (searchParams) {
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, value)
        }
      })
    }

    const response = await this.client.basicGet<NovaServersResponse>({
      url: `/servers/detail?${queryParams.toString()}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getInstancesPaginated',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.servers
  }

  searchInstanceAcrossTenants = async ({ query, property }) => {
    const response = await this.client.basicGet<NovaServersResponse>({
      url: `/servers/detail?all_tenants=True&${property}=${query}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'searchAllOpenstackVms',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.servers
  }

  getInstancesWithTag = async (tag) => {
    const response = await this.client.basicGet<NovaServersResponse>({
      url: `/servers/detail?all_tenants=True&tags=${tag}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getTagOpenstackVms',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.servers
  }

  getInstance = async (instanceId) => {
    const response = await this.client.basicGet<any>({
      url: `/servers/${instanceId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackVm',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.server
  }

  createInstance = async (body) => {
    const response = await this.client.basicPost<any>({
      url: `/servers`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createOpenstackVm',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response.server
  }

  createInstances = async (body) => {
    const response = await this.client.basicPost<any>({
      url: `/servers`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createOpenstackVms',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response
  }

  getInstaceDetails = async (id) => {
    const url = `/servers/${id}`
    const response = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getInstaceDetail',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.server
  }

  updateInstance = async (id, body) => {
    const projectId = this.client.activeProjectId
    const url = `/servers/${id}`
    const response = await this.client.basicPut<any>({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateOpenstackVm',
      },
    })
    return response.server
  }

  setInstanceMetadata = async ({ id, body }) => {
    const response = await this.client.basicPut<any>({
      url: `/servers/${id}/metadata`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateOpenstackVmMetadata',
      },
    })
    return response.metadata
  }

  addInstanceTag = async ({ id, tag }) => {
    const response = await this.client.basicPut<any>({
      url: `/servers/${id}/tags/${tag}`,
      body: null,
      options: {
        clsName: this.getClassName(),
        mthdName: 'addOpenstackVmTag',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response
  }

  deleteInstance = async (id) => {
    return this.client.basicDelete({
      url: `/servers/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteOpenstackVm',
      },
    })
  }

  async getServerGroups() {
    const response = await this.client.basicGet<any>({
      url: `/os-server-groups`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getServerGroups',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response.server_groups
  }

  createServerGroup = async (body) => {
    const response = await this.client.basicPost<any>({
      url: `/os-server-groups`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createServerGroup',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response.server_group
  }

  deleteServerGroup = async (id) => {
    return this.client.basicDelete({
      url: `/os-server-groups/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteServerGroup',
      },
    })
  }

  async getHypervisors() {
    // const url = `${this.hypervisorsUrl()}/detail`
    const response = await this.client.basicGet<any>({
      url: `/os-hypervisors/detail`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getHypervisors',
      },
    })
    return response.hypervisors
  }

  getHypervisor = async (id) => {
    const response = await this.client.basicGet<any>({
      url: `/os-hypervisors/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getHypervisor',
      },
    })
    return response.hypervisor
  }

  enableHypervisorScheduling = async (body) => {
    const response = await this.client.basicPut<any>({
      url: '/os-services/enable',
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'enableHypervisorScheduling',
      },
    })
    return response
  }

  disableHypervisorScheduling = async (body) => {
    const response = await this.client.basicPut<any>({
      url: '/os-services/disable',
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'disableHypervisorScheduling',
      },
    })
    return response
  }

  enableMaintenanceMode = async (body) => {
    const response = await this.client.basicPut<any>({
      url: '/os-services/disable-log-reason',
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'enableMaintenanceMode',
      },
    })
    return response
  }

  async getHostAggregates() {
    const response = await this.client.basicGet<any>({
      url: `/os-aggregates`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getHostAggregates',
      },
    })
    return response.aggregates
  }

  createHostAggregate = async (body) => {
    const response = await this.client.basicPost<any>({
      url: `/os-aggregates`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createHostAggregate',
      },
    })
    return response.aggregate
  }

  getHostAggregate = async (id) => {
    const response = await this.client.basicGet<any>({
      url: `/os-aggregates/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getHostAggregate',
      },
    })
    return response.aggregate
  }

  updateHostAggregate = async ({ id, body }) => {
    const response = await this.client.basicPut<any>({
      url: `/os-aggregates/${id}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateHostAggregate',
      },
    })
    return response.aggregate
  }

  hostAggregateAction = async ({ id, body }) => {
    const response = await this.client.basicPost<any>({
      url: `/os-aggregates/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'hostAggregateAction',
      },
    })
    return response.aggregate
  }

  deleteHostAggregate = async (id) => {
    return this.client.basicDelete<any>({
      url: `/os-aggregates/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteHostAggregate',
      },
    })
  }

  async getAvailabilityZones() {
    const response = await this.client.basicGet<any>({
      url: `/os-availability-zone/detail`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getAvailabilityZones',
      },
    })
    return response.availabilityZoneInfo
  }

  async getSshKeys() {
    const response = await this.client.basicGet<any>({
      url: `/os-keypairs`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getSshKeys',
      },
    })
    return response.keypairs
  }

  async getSshKey(name) {
    const response = await this.client.basicGet<any>({
      url: `/os-keypairs/${name}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getSshKey',
      },
    })
    return response.keypair
  }

  async getSshKeysForUser({ userId }) {
    const response = await this.client.basicGet<any>({
      url: `/os-keypairs?user_id=${userId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getSshKeysForUser',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response.keypairs
  }

  createSshKey = async (body) => {
    const response = await this.client.basicPost<any>({
      url: `/os-keypairs`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createSshKeys',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response.keypair
  }

  createSshKeyForUser = async ({ body }) => {
    const response = await this.client.basicPost<any>({
      url: `/os-keypairs`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createSshKeyForUser',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response.keypair
  }

  async deleteSshKey(name) {
    return this.client.basicDelete<any>({
      url: `/os-keypairs/${name}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteSshKey',
      },
    })
  }

  async deleteSshKeyForUser({ name, userId }) {
    return this.client.basicDelete<any>({
      url: `/os-keypairs/${name}?user_id=${userId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteSshKeyForUser',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
  }

  instanceAction = async ({ id, body }) => {
    return this.client.basicPost<any>({
      url: `/servers/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'openstackInstanceAction',
      },
    })
  }

  instanceActionWithSpecificVersion = async ({ id, body }) => {
    return this.client.basicPost<any>({
      url: `/servers/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'instanceActionWithVersion',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
  }

  attachVolume = async ({ vmId, body }) => {
    return this.client.basicPost<any>({
      url: `/servers/${vmId}/os-volume_attachments`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'attachVolumeToInstance',
      },
    })
  }

  detachVolume = async ({ vmId, volumeId }) => {
    return this.client.basicDelete<any>({
      url: `/servers/${vmId}/os-volume_attachments/${volumeId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'detachVolumeFromInstance',
      },
    })
  }

  updateVolumeAttachment = async ({ id, volumeId, body }) => {
    return this.client.basicPut<any>({
      url: `/servers/${id}/os-volume_attachments/${volumeId}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateVolumeAttachment',
        config: {
          headers: {
            // Passed subversion here, since delete on termination is not supported to default 2.1 version.
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
  }

  createConsole = async ({ vmId }) => {
    // Try microversion 2.95 and use remote_consoles endpoint
    return this.client.basicPost<any>({
      url: `/servers/${vmId}/remote-consoles`,
      // url: `/servers/${vmId}/action`,
      // body: {
      //   'os-getVNCConsole': {
      //     type: 'novnc',
      //   },
      // },
      body: {
        remote_console: {
          protocol: 'vnc',
          type: 'novnc',
        },
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'createVmConsole',
        config: {
          headers: {
            // 'OpenStack-API-Version': 'compute 2.87', // sbux-scaletest
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
  }

  addSecurityGroupAction = async ({ id, securityGroupName }) => {
    return this.client.basicPost<any>({
      url: `/servers/${id}/action`,
      body: {
        addSecurityGroup: {
          name: securityGroupName,
        },
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'openstackInstanceAction',
      },
    })
  }

  removeSecurityGroupAction = async ({ id, securityGroupName }) => {
    return this.client.basicPost<any>({
      url: `/servers/${id}/action`,
      body: {
        removeSecurityGroup: {
          name: securityGroupName,
        },
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'openstackInstanceAction',
      },
    })
  }

  rescueVmAction = async ({ id, body }) => {
    return this.client.basicPost<any>({
      url: `/servers/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'rescueVmAction',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
  }

  unrescueVmAction = async ({ id }) => {
    return this.client.basicPost<any>({
      url: `/servers/${id}/action`,
      body: {
        unrescue: null,
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'unrescueVmAction',
      },
    })
  }

  createInterface = async ({ id, body }) => {
    return this.client.basicPost<any>({
      url: `/servers/${id}/os-interface`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createVmInterface',
      },
    })
  }

  detachInterface = async ({ id, portId }) => {
    return this.client.basicDelete<any>({
      url: `/servers/${id}/os-interface/${portId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'detachInterfaceFromVm',
      },
    })
  }

  getQuotas = async ({ id }) => {
    const response = await this.client.basicGet<any>({
      url: `/os-quota-sets/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getNovaQuotas',
      },
    })
    return response.quota_set
  }

  updateQuotas = async ({ id, body }) => {
    const response = await this.client.basicPut<any>({
      url: `/os-quota-sets/${id}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateNovaQuotas',
      },
    })
    return response.quota_set
  }

  getQuotaUsage = async ({ id }) => {
    const response = await this.client.basicGet<any>({
      url: `/os-quota-sets/${id}/detail`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getQuotaUsage',
      },
    })
    return response.quota_set
  }

  getQuotasForUser = async ({ id, userId }) => {
    const response = await this.client.basicGet<any>({
      url: `/os-quota-sets/${id}/detail?user_id=${userId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getNovaQuotasForUser',
      },
    })
    return response.quota_set
  }

  getSimpleTenantUsage = async ({ id }) => {
    const response = await this.client.basicGet<any>({
      url: `/os-simple-tenant-usage/${id}?detailed=1`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getSimpleTenantUsage',
      },
    })
    return response.tenant_usage
  }

  updateQuotasForUser = async ({ id, userId, body }) => {
    const response = await this.client.basicPut<any>({
      url: `/os-quota-sets/${id}?user_id=${userId}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateNovaQuotasForUser',
      },
    })
    return response.quota_set
  }

  getVmEvents = async ({ id }) => {
    const response = await this.client.basicGet<any>({
      url: `/servers/${id}/os-instance-actions`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getVmEvents',
      },
    })
    return response.instanceActions
  }

  getVmEventDetails = async ({ vmId, requestId }) => {
    const response = await this.client.basicGet<any>({
      url: `/servers/${vmId}/os-instance-actions/${requestId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getVmEventDetails',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.95',
          },
        },
      },
    })
    return response.instanceAction
  }

  async getComputeServiceDetails() {
    const response = await this.client.basicGet<any>({
      url: `/os-services`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getComputeServiceDetails',
      },
    })
    return response.services
  }

  updateVirtualMachineOwner = async ({ id, body }) => {
    return await this.client.basicPost<any>({
      url: `/servers/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateVirtualMachineOwner',
      },
    })
  }

  getMigrations = async () => {
    const response = await this.client.basicGet<any>({
      url: `/os-migrations`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getMigrations',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.migrations
  }

  getMigrationsSince = async (timestamp) => {
    const response = await this.client.basicGet<any>({
      url: `/os-migrations?changes-since=${timestamp}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getMigrationsSince',
        config: {
          headers: {
            'OpenStack-API-Version': 'compute 2.96',
          },
        },
      },
    })
    return response.migrations
  }
}

export default Nova
